// @ts-strict-ignore
import type { ColumnDef } from '@usealloy/component-library';
import type { MatchData } from '../../types';
import { allTableColumns } from '../../constants';
import { isResolvable } from '../ResolutionModal/helper';

type ColumnDefWithAccessor = ColumnDef<MatchData> & {
  accessorFn: (MatchData) => any;
};
type ColumnCustomValidations = { [key: string]: (value: any) => boolean };

type PreventAvailableDegradationIndex = {
  [key: string]: {
    availableFilters: { [key: string]: boolean };
    availableColumns: ColumnDef<MatchData>[];
  };
};
const preventAvailableDegradation: PreventAvailableDegradationIndex = {};

export const calculateAvailableColumns = (
  cacheKey: string,
  columns: ColumnDef<MatchData>[],
  matches: MatchData[],
  validations: ColumnCustomValidations = {},
) => {
  const availableFilters = allTableColumns.reduce((s, column) => {
    s[column.id] = false;
    return s;
  }, {} as { [key: string]: boolean });

  const availableColumns = columns.filter((
    column: ColumnDefWithAccessor,
    idx: Number,
  ) => {
    // passing through all cell values of the column to see if there's any value
    const shouldDisplayColumn = matches.filter((
      row: MatchData,
    ) => {
      const value = column.accessorFn(row);
      let available = true;
      if (validations[column.id]) {
        available = validations[column.id](value);
      } else {
        switch (typeof value) {
          case 'string': available = value.length > 0; break;
          case 'object':
            available = Array.isArray(value) ? value.length > 0 : value !== null;
            break;
          default: available = value !== null;
        }
      }
      availableFilters[column.id] = available;
      return available;
    }).length > 0;

    if (idx === 0) {
      /**
       * in case of a table without data, the first column will be injected so it won't break the layout,
       * but no filter will be shown
       */
      return true;
    }

    return shouldDisplayColumn;
  });

  let output = {
    availableFilters,
    availableColumns,
  };

  // bugfix: when the filters are used and we have no hit, the filter will disappear
  if (preventAvailableDegradation[cacheKey]) {
    const previous = preventAvailableDegradation[cacheKey];
    if (previous.availableColumns.length > output.availableColumns.length) {
      output = previous;
    }
  }
  preventAvailableDegradation[cacheKey] = output;
  return output;
};

export const calculateWatchlistTableProps = (filteredMatches: MatchData[]) => {
  let hasResolvableServices = false;
  let disabledReviewTab = true;

  filteredMatches
    .forEach(match => {
      const { status } = match;
      if (!hasResolvableServices && isResolvable(match)) {
        hasResolvableServices = true;
      }
      if (disabledReviewTab && status === 'Needs Review') {
        disabledReviewTab = false;
      }
    });

  return {
    hasResolvableServices,
    disabledReviewTab,
  };
};
